package com.example.borshi.web.nutritionist.ui

import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Common UI components used across the Nutritionist module
 * Part of the Nutritionist Module - can be extracted as independent module
 */

@Composable
fun LoadingSpinner() {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            minHeight(400.px)
            flexDirection(FlexDirection.Column)
            gap(16.px)
        }
    }) {
        Div({
            style {
                fontSize(48.px)
                property("animation", "spin 1s linear infinite")
            }
        }) {
            Text("⏳")
        }
        Div({
            style {
                fontSize(18.px)
                color(Color("#666"))
            }
        }) {
            Text("Loading...")
        }
    }

    // Add animation keyframes
    Style {
        """
        @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
        }
        """.trimIndent()
    }
}

@Composable
fun ErrorMessage(message: String, onRetry: (() -> Unit)? = null) {
    Div({
        style {
            backgroundColor(Color("#FFEBEE"))
            padding(20.px, 24.px)
            borderRadius(8.px)
            property("border-left", "4px solid #F44336")
            marginBottom(20.px)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            gap(16.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(12.px)
            }
        }) {
            Span({ style { fontSize(24.px) } }) {
                Text("❌")
            }
            Span({
                style {
                    color(Color("#D32F2F"))
                    fontSize(16.px)
                }
            }) {
                Text(message)
            }
        }

        onRetry?.let { retry ->
            Button({
                style {
                    padding(8.px, 16.px)
                    backgroundColor(Color("#F44336"))
                    color(Color.white)
                    border(0.px)
                    borderRadius(6.px)
                    cursor("pointer")
                    fontSize(14.px)
                    fontWeight("500")
                }
                onClick { retry() }
            }) {
                Text("Retry")
            }
        }
    }
}

@Composable
fun SuccessMessage(message: String) {
    Div({
        style {
            backgroundColor(Color("#E8F5E9"))
            padding(20.px, 24.px)
            borderRadius(8.px)
            property("border-left", "4px solid #4CAF50")
            marginBottom(20.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(12.px)
        }
    }) {
        Span({ style { fontSize(24.px) } }) {
            Text("✅")
        }
        Span({
            style {
                color(Color("#2E7D32"))
                fontSize(16.px)
            }
        }) {
            Text(message)
        }
    }
}

@Composable
fun InfoMessage(message: String) {
    Div({
        style {
            backgroundColor(Color("#E3F2FD"))
            padding(20.px, 24.px)
            borderRadius(8.px)
            property("border-left", "4px solid #2196F3")
            marginBottom(20.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(12.px)
        }
    }) {
        Span({ style { fontSize(24.px) } }) {
            Text("ℹ️")
        }
        Span({
            style {
                color(Color("#1565C0"))
                fontSize(16.px)
            }
        }) {
            Text(message)
        }
    }
}

@Composable
fun EmptyState(
    icon: String = "📭",
    title: String,
    message: String,
    actionLabel: String? = null,
    onAction: (() -> Unit)? = null
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            minHeight(400.px)
            padding(40.px)
            textAlign("center")
        }
    }) {
        Div({ style { fontSize(64.px); marginBottom(16.px) } }) {
            Text(icon)
        }
        H3({
            style {
                fontSize(24.px)
                marginBottom(8.px)
                color(Color("#333"))
            }
        }) {
            Text(title)
        }
        P({
            style {
                fontSize(16.px)
                color(Color("#666"))
                marginBottom(24.px)
                maxWidth(400.px)
            }
        }) {
            Text(message)
        }

        if (actionLabel != null && onAction != null) {
            Button({
                style {
                    padding(12.px, 24.px)
                    backgroundColor(Color("#2196F3"))
                    color(Color.white)
                    border(0.px)
                    borderRadius(8.px)
                    fontSize(16.px)
                    fontWeight("500")
                    cursor("pointer")
                }
                onClick { onAction() }
            }) {
                Text(actionLabel)
            }
        }
    }
}

@Composable
fun SearchBar(
    value: String,
    placeholder: String = "Search...",
    onValueChange: (String) -> Unit
) {
    Div({
        style {
            marginBottom(20.px)
        }
    }) {
        Input(InputType.Text) {
            value(value)
            onInput { event ->
                onValueChange(event.value)
            }
            attr("placeholder", placeholder)
            style {
                width(100.percent)
                padding(12.px, 16.px)
                fontSize(16.px)
                border(1.px, LineStyle.Solid, Color("#ddd"))
                borderRadius(8.px)
                property("box-sizing", "border-box")
            }
        }
    }
}

@Composable
fun Card(content: @Composable () -> Unit) {
    Div({
        style {
            backgroundColor(Color.white)
            padding(24.px)
            borderRadius(12.px)
            property("box-shadow", "0 2px 8px rgba(0,0,0,0.1)")
        }
    }) {
        content()
    }
}

